import React, { useEffect } from 'react';
import { useState } from 'react';
import MoneyLover from './Pages/MoneyLover.js';
import MoneyLoverEdit from './Pages/MoneyLoverEdit';
import MoneyAnalystic from './Pages/MoneyAnalystic';
import GoldPrice from './Pages/GoldPrice';
import TestFcm from './Pages/TestFcm';
import Login from './Pages/Login';
import Profile from './Pages/Profile';
import { BrowserRouter as Router, Link, Route, Routes } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import { onForegroundMessage, onMessageListener, requestPermission } from './firebase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHome, FaChartLine, FaCoins, FaUser, FaBars, FaTimes } from 'react-icons/fa';

import './App.css';

const BE_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const menuItems = [
  { url: '/', title: 'Home', icon: <FaHome className="mr-2" /> },
  { url: '/money-lover-analytic', title: 'Analytics', icon: <FaChartLine className="mr-2" /> },
  { url: '/gold-price', title: 'Gold Price', icon: <FaCoins className="mr-2" /> },
  { url: '/profile', title: 'My Profile', icon: <FaUser className="mr-2" /> },
];

const menuVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const menuItemVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0 }
};


function App() {
  const [showMenu, setShowMenu] = useState(false);
  const pathname = window.location.pathname
  const [isLoginPage, setIsLoginPage] = useState(pathname.includes('/login'))

  const ToastifyNotification = ({ title, body }) => (
    <div className="push-notification">
      <h2 className="push-notification-title">{title}</h2>
      <p className="push-notification-text">{body}</p>
    </div>
  )

  const createFcmToken = (token) => {
    fetch(`${BE_API_URL}/api/v1/notifications/createFcmToken`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ fcmToken: token })
    })
      .then(response => response.json())
      .then(data => {
        console.info('Update fcm token successful!')
      })
      .catch(error => {
        console.error(error);
      });
  }

  const showNotification = (title, body) => {
    return toast.info(<ToastifyNotification title={title} body={body} />, {
      position: "top-right",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
  }

  useEffect(() => {
    console.log('isLoginPage', isLoginPage)
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    if (!isLoginPage && (!accessToken || !refreshToken)) {
      window.location.href = '/login';
    } else if (accessToken && refreshToken) {
      setIsLoginPage(false);
    }
    const fcmToken = localStorage.getItem("fcmToken");
    if (fcmToken) {
      console.log('FCM Token:', fcmToken)
      onForegroundMessage()
        .then((payload) => {
          console.log('Received foreground message: ', payload);
          const { notification: { title, body } } = payload;
          showNotification(title, body);
        })
        .catch(err => console.log('An error occured while retrieving foreground message. ', err));
      
      onMessageListener().then((payload) => {
          console.log('Message received. ', payload);
          const { title, body, icon } = payload.notification;
          showNotification(title, body);
          new Notification(title, { body, icon });
        }).catch((err) => console.log('Failed to receive message:', err));
    } else {
      requestPermission().then((token) => {
        // Send the token to your server
        console.log('FCM Token:', token);
        createFcmToken(token);
      }).catch((err) => {
        console.error('Error during permission request or token retrieval:', err);
      });
    }
  }, [isLoginPage]);

  const renderHeader = () => {
    return (
      <div className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg">
        <nav className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-white text-2xl font-bold hover:text-indigo-100 transition-colors duration-200"
            >
              <span className="bg-white text-indigo-600 rounded-lg p-1.5">
                <FaCoins className="h-6 w-6" />
              </span>
              <span className="hidden sm:inline-block">Ginger Finance</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {menuItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.url}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    window.location.pathname === item.url
                      ? 'bg-white/20 text-white'
                      : 'text-indigo-100 hover:bg-white/10'
                  }`}
                >
                  {item.icon}
                  {item.title}
                </Link>
              ))}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 rounded-lg text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50"
                aria-label="Toggle menu"
              >
                {showMenu ? (
                  <FaTimes className="h-6 w-6" />
                ) : (
                  <FaBars className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={menuVariants}
                className="lg:hidden pt-4 pb-2"
              >
                <motion.div variants={menuVariants} className="flex flex-col space-y-2">
                  {menuItems.map((item, index) => (
                    <motion.div key={index} variants={menuItemVariants}>
                      <Link
                        to={item.url}
                        onClick={() => setShowMenu(false)}
                        className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors duration-200 ${
                          window.location.pathname === item.url
                            ? 'bg-white/20 text-white'
                            : 'text-indigo-100 hover:bg-white/10'
                        }`}
                      >
                        {item.icon}
                        {item.title}
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>
      </div>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Router>
        <div className='flex flex-col min-h-screen bg-gray-50'>
          {!isLoginPage && renderHeader()}
          <main className="flex-grow container mx-auto px-4 py-6">
            <Routes>
              <Route path="/" element={<MoneyLover />} />
              <Route path="/login" element={<Login />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/gold-price" element={<GoldPrice />} />
              <Route path="/money-lover" element={<MoneyLover />} />
              <Route path="/money-lover-edit" element={<MoneyLoverEdit />} />
              <Route path="/money-lover-analytic" element={<MoneyAnalystic />} />
              <Route path='/test-fcm' element={<TestFcm />} />
            </Routes>
          </main>
        </div>
      </Router>
      <ToastContainer hideProgressBar />
    </LocalizationProvider>
  );
}

export default App;
